import { useState, useCallback, useEffect, useRef, useMemo, type RefCallback } from 'react'
import { ToastController } from '@grofers/dock-design-kit'
import type { Brand } from '@/types/brand'
import {
  useRequestBrandAccess,
  useFetchAvailableBrands,
} from '@/entities/brandRequest/brandRequest'
import type { AvailableBrandsPagination } from '@/entities/brandRequest/types'
import {
  BRAND_ACCESS_STATUS,
  BRAND_SELECTION_MODAL_TEXT,
  BRAND_SELECTION_PAGE_SIZE,
} from '@/constants/commonConstants'
import { useDebounce } from '../common/useDebounce'
import { trackButtonClicked } from '@/analytics'
import { PAGE_NAMES, WIDGET_TYPES, WIDGET_NAMES, EVENT_STATUS } from '@/constants/jumboEventNames'
import { useLocation } from 'react-router'
import { APP_ROUTES } from '@/routes/routes.constants'
import type {
  UseBrandSelectionModalDataProps,
  UseBrandSelectionModalDataReturn,
} from './brand.types'

const getBrandFetchErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Something went wrong while fetching brands. Please try again.'
  }

  if (typeof error === 'string') {
    return error
  }

  if (typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message
  }

  return 'Failed to fetch brands. Please try again.'
}

const deriveNextPageNumber = (
  pagination: AvailableBrandsPagination | undefined,
  currentPage: number,
  pageSize: number,
  receivedCount: number
): number | null => {
  if (!pagination) {
    return receivedCount < pageSize ? null : currentPage + 1
  }

  const nextFromResponse = parseInt(pagination.next_page_number ?? '', 10)
  if (!Number.isNaN(nextFromResponse) && nextFromResponse > currentPage) {
    return nextFromResponse
  }

  const totalPages = parseInt(pagination.total_pages, 10)
  if (!Number.isNaN(totalPages)) {
    const candidate = currentPage + 1
    if (candidate > totalPages) {
      return null
    }
    return candidate
  }

  const totalRecords = parseInt(pagination.total_records, 10)
  const pageSizeFromResponse = parseInt(pagination.page_size, 10)
  if (!Number.isNaN(totalRecords) && !Number.isNaN(pageSizeFromResponse)) {
    const maxPage = Math.ceil(totalRecords / pageSizeFromResponse)
    const candidate = currentPage + 1
    if (candidate > maxPage) {
      return null
    }
    return candidate
  }

  return receivedCount < pageSize ? null : currentPage + 1
}

export const useBrandSelectionModalData = ({
  isOpen,
  onClose,
  onRequestAccess,
  isLoading = false,
}: UseBrandSelectionModalDataProps): UseBrandSelectionModalDataReturn => {
  const { pathname } = useLocation()
  const {
    requestBrandAccessAsync,
    isLoading: isRequestingAccess,
    error: requestError,
  } = useRequestBrandAccess()
  const { fetchAvailableBrandsAsync } = useFetchAvailableBrands()

  const [selectedBrands, setSelectedBrands] = useState<Brand[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [apiError, setApiError] = useState<string | null>(null)
  const [isBrandsExpanded, setIsBrandsExpanded] = useState(false)
  const [availableBrandOptions, setAvailableBrandOptions] = useState<Brand[]>([])
  const [isFetchingBrands, setIsFetchingBrands] = useState(false)
  const [brandFetchError, setBrandFetchError] = useState<string | null>(null)
  const [viewOnlySelected, setViewOnlySelected] = useState(false)
  const [maxBrandsPerRequest, setMaxBrandsPerRequest] = useState<number>(10)
  const isFetchingRef = useRef(false)
  const nextPageRef = useRef<number | null>(1)
  const debouncedSearchTermRef = useRef('')
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 400)
  const currentPageName = useMemo(() => {
    if (pathname.startsWith(APP_ROUTES.META_COLLAB)) {
      return PAGE_NAMES.META_COLLAB
    }

    return PAGE_NAMES.HOME
  }, [pathname])

  useEffect(() => {
    if (!isOpen) {
      setSelectedBrands([])
      setSearchTerm('')
      setApiError(null)
      setIsBrandsExpanded(false)
      setAvailableBrandOptions([])
      setBrandFetchError(null)
      setViewOnlySelected(false)
      nextPageRef.current = 1
      isFetchingRef.current = false
    }
  }, [isOpen])

  useEffect(() => {
    if (requestError) {
      const errorMessage =
        requestError && typeof requestError === 'object' && 'message' in requestError
          ? requestError.message
          : 'Failed to request brand access. Please try again.'
      setApiError(errorMessage)
    }
  }, [requestError])

  const fetchBrands = useCallback(
    async (pageNumber: number, searchValue: string, isLoadMore = false) => {
      if (isFetchingRef.current) {
        return
      }

      isFetchingRef.current = true
      setIsFetchingBrands(true)
      setBrandFetchError(null)

      try {
        const response = await fetchAvailableBrandsAsync({
          pageNumber,
          pageSize: BRAND_SELECTION_PAGE_SIZE,
          search: searchValue || undefined,
        })
        if (response) {
          const rawBrands = response.available_brands
          const transformedBrands = Array.isArray(rawBrands)
            ? rawBrands
                .filter(
                  (brand): brand is NonNullable<typeof brand> => brand != null && brand.id != null
                )
                .map((brand) => ({
                  id: brand.id,
                  name: brand.name ?? '',
                  logo: brand.logo,
                  hasAccess: brand.has_access === true,
                }))
            : []

          if (response.max_brands_per_request != null) {
            setMaxBrandsPerRequest(response.max_brands_per_request)
          }

          setAvailableBrandOptions((prev) =>
            isLoadMore ? [...prev, ...transformedBrands] : transformedBrands
          )

          const nextPage = deriveNextPageNumber(
            response.pagination,
            pageNumber,
            BRAND_SELECTION_PAGE_SIZE,
            transformedBrands.length
          )
          nextPageRef.current = nextPage
        }
      } catch (error) {
        setBrandFetchError(getBrandFetchErrorMessage(error))
        if (!isLoadMore) {
          setAvailableBrandOptions([])
        }
      } finally {
        isFetchingRef.current = false
        setIsFetchingBrands(false)
      }
    },
    [fetchAvailableBrandsAsync]
  )

  useEffect(() => {
    debouncedSearchTermRef.current = debouncedSearchTerm
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (!isOpen || viewOnlySelected) {
      return
    }
    nextPageRef.current = 1
    setAvailableBrandOptions([])
    fetchBrands(1, debouncedSearchTerm)
  }, [debouncedSearchTerm, isOpen, viewOnlySelected, fetchBrands])

  const handleBrandToggle = useCallback(
    (brand: Brand) => {
      if (brand.hasAccess) {
        return
      }
      setSelectedBrands((prev) => {
        const isSelected = prev.some((selected) => selected.id === brand.id)
        if (isSelected) {
          return prev.filter((selected) => selected.id !== brand.id)
        }

        if (prev.length >= maxBrandsPerRequest) {
          ToastController.notify({
            hierarchy: 'low',
            headlineContent: 'Limit reached',
            paragraphContent: `You can select up to ${maxBrandsPerRequest} brands only.`,
            toastPosition: 'bottom-left',
            variant: 'warning',
            iconName: 'info',
          })
          return prev
        }

        return [...prev, brand]
      })
      if (apiError) {
        setApiError(null)
      }
    },
    [apiError, maxBrandsPerRequest]
  )

  useEffect(() => {
    if (selectedBrands.length === 0 && viewOnlySelected) {
      fetchBrands(1, debouncedSearchTermRef.current, true)
      setViewOnlySelected(false)
    }
  }, [selectedBrands, viewOnlySelected, fetchBrands])

  const handleToggleViewSelected = useCallback(() => {
    setViewOnlySelected((prev) => !prev)
  }, [])

  const handleRequestAccess = useCallback(async () => {
    if (selectedBrands.length === 0) {
      return
    }
    setApiError(null)
    try {
      const response = await requestBrandAccessAsync({
        brands: selectedBrands.map((brand) => ({
          id: brand.id,
          name: brand.name,
        })),
      })
      if (response) {
        trackButtonClicked({
          widget_type: WIDGET_TYPES.BUTTON,
          widget_name: WIDGET_NAMES.SUBMIT_BRAND_ACCESS,
          page_name: currentPageName,
          event_status: EVENT_STATUS.EVENT_STATUS_SUCCESS,
          brands: selectedBrands.map((b) => ({ id: String(b.id), name: b.name })),
        })
        onRequestAccess(response.status, response.requested_brands)
        if (response.status !== BRAND_ACCESS_STATUS.PENDING) {
          onClose()
        }
      }
    } catch {
      trackButtonClicked({
        widget_type: WIDGET_TYPES.BUTTON,
        widget_name: WIDGET_NAMES.SUBMIT_BRAND_ACCESS,
        page_name: currentPageName,
        event_status: EVENT_STATUS.EVENT_STATUS_FAILURE,
        brands: selectedBrands.map((b) => ({ id: String(b.id), name: b.name })),
      })
    }
  }, [selectedBrands, requestBrandAccessAsync, onRequestAccess, onClose, currentPageName])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  useEffect(() => {
    observerRef.current?.disconnect()

    if (viewOnlySelected || !isOpen) {
      return
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting || isFetchingRef.current || !nextPageRef.current) {
          return
        }
        fetchBrands(nextPageRef.current, debouncedSearchTermRef.current, true)
      },
      { threshold: 0.1 }
    )

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current)
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [viewOnlySelected, isOpen, fetchBrands])

  const loadMoreRef: RefCallback<HTMLDivElement> = useCallback((node: HTMLDivElement | null) => {
    sentinelRef.current = node

    if (node && observerRef.current) {
      observerRef.current.observe(node)
    }
  }, [])

  const handleExpandBrands = useCallback(() => {
    setIsBrandsExpanded(true)
  }, [])

  const handleCollapseBrands = useCallback(() => {
    setIsBrandsExpanded(false)
  }, [])

  const isRequestButtonEnabled = selectedBrands?.length > 0 && !isLoading && !isRequestingAccess
  const isInitialBrandsLoading = isFetchingBrands && availableBrandOptions.length === 0
  const shouldShowEmptyState = !isInitialBrandsLoading && availableBrandOptions.length === 0
  const shouldShowLoadMoreSpinner = isFetchingBrands && availableBrandOptions.length > 0
  const isAtLimit = selectedBrands.length >= maxBrandsPerRequest

  const displayedBrands = useMemo(() => {
    if (viewOnlySelected) {
      return selectedBrands
    }
    return availableBrandOptions
  }, [viewOnlySelected, selectedBrands, availableBrandOptions])

  const { processingButton: processingButtonLabel, requestAccessButton: requestAccessButtonLabel } =
    BRAND_SELECTION_MODAL_TEXT

  return {
    state: {
      selectedBrands,
      searchTerm,
      apiError,
      isBrandsExpanded,
      isRequestButtonEnabled,
      isRequestingAccess,
      brandFetchError,
      isInitialBrandsLoading,
      shouldShowEmptyState,
      shouldShowLoadMoreSpinner,
      processingButtonLabel,
      requestAccessButtonLabel,
      viewOnlySelected,
      displayedBrands,
      maxBrandsPerRequest,
      isAtLimit,
    },
    action: {
      handleBrandToggle,
      handleRequestAccess,
      handleInputChange,
      handleExpandBrands,
      handleCollapseBrands,
      handleToggleViewSelected,
      loadMoreRef,
    },
  }
}
